import type { GridQueryInput } from './gridUtils';

export type GridFilters = GridQueryInput['filters'];

export const buildEqFilter = (field: string, value: string | null | undefined): GridFilters =>
  value ? [{ field, operator: 'eq', value }] : [];

export const buildEqFilterValue = (
  field: string,
  value: string | number | boolean | null | undefined,
): GridFilters => (value !== null && value !== undefined ? [{ field, operator: 'eq', value }] : []);

export const buildSlugFilters = (slug: string | null | undefined): GridFilters =>
  buildEqFilter('slug', slug ?? null);

export const buildInFilter = (field: string, values: string[]): GridFilters =>
  values.length > 0 ? [{ field, operator: 'in', value: values }] : [];

export const buildInFilterWithNull = (field: string, values: Array<string | null>): GridFilters =>
  values.length > 0 ? [{ field, operator: 'in', value: values }] : [];

export const buildNeqFilters = (
  field: string,
  values: Array<string | number | boolean>,
): GridFilters =>
  values.length > 0 ? values.map((value) => ({ field, operator: 'neq' as const, value })) : [];

export const buildGteFilter = (
  field: string,
  value: string | number | null | undefined,
): GridFilters =>
  value !== null && value !== undefined ? [{ field, operator: 'gte', value }] : [];

export const buildLteFilter = (
  field: string,
  value: string | number | null | undefined,
): GridFilters =>
  value !== null && value !== undefined ? [{ field, operator: 'lte', value }] : [];

export const buildDateRangeFilters = (
  field: string,
  start: string | number | null | undefined,
  end: string | number | null | undefined,
): GridFilters => mergeFilters(buildGteFilter(field, start), buildLteFilter(field, end));

export const buildLatestDateRangeFilters = (
  field: string,
  start: string | number | null | undefined,
  end: string | number | null | undefined,
): GridFilters =>
  mergeFilters(buildEqFilterValue('isLatest', true), buildDateRangeFilters(field, start, end));

export const buildContainsFilter = (
  field: string,
  value: string | null | undefined,
): GridFilters => {
  const trimmed = value?.trim();
  return trimmed ? [{ field, operator: 'contains', value: trimmed }] : [];
};

export const buildStartsWithFilter = (
  field: string,
  value: string | null | undefined,
): GridFilters => {
  const trimmed = value?.trim();
  return trimmed ? [{ field, operator: 'startsWith', value: trimmed }] : [];
};

export const buildReleaseIdFilters = (releaseId: string | null | undefined): GridFilters =>
  buildEqFilter('releaseId', releaseId);

export const buildProjectScopedFilters = (projectId: string | null | undefined): GridFilters =>
  buildEqFilterValue('projectId', projectId);

export const buildOrganisationFilters = (
  organisationId: string | null | undefined,
): GridFilters | undefined =>
  organisationId ? buildEqFilter('organisationId', organisationId) : undefined;

export const buildProjectFilters = ({
  projectId,
  organisationId,
  allowedProjectIds,
}: {
  projectId?: string | null;
  organisationId?: string | null;
  allowedProjectIds?: Set<string> | string[];
}): GridFilters | undefined => {
  if (projectId) {
    return buildEqFilter('projectId', projectId);
  }
  if (organisationId === null) {
    const ids = Array.isArray(allowedProjectIds)
      ? [...allowedProjectIds]
      : Array.from(allowedProjectIds ?? []);
    const resolvedIds = ids.sort((left, right) => left.localeCompare(right));
    if (resolvedIds.length > 0) {
      return buildInFilter('projectId', resolvedIds);
    }
  }
  return undefined;
};

export const mergeFilters = (...filters: Array<GridFilters | undefined>): GridFilters =>
  filters.flatMap((filterSet) => filterSet ?? []);
